import { Fragment, useEffect, useRef } from "react";
import { Button, Empty, Flex, Form, Modal, ModalProps, notification, Steps, Upload, UploadProps } from "antd";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/lib/table";

import SupplierSelect from "@/pages/Purchase/components/SupplierSelect";
import { productList } from "@/apis/product";
import { Product } from "@/types/product";
import { parseData, ParsedProQty } from "./utils";
import STableLayout from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { AnyObject } from "antd/es/_util/type";
import { batchInOrderAdd, itemFetchs } from "@/apis/purchase";
import { BatchInOrderAdd, PurchasefFnishStateEnum, PurchaseOrderItem } from "@/types/purchase";
import { ChangeEnum, ConfirmedEnum, OperateEnum } from "@/types/inventory";
import { DeleteOutlined } from "@ant-design/icons";
import { OrderAddItemV2, OrderAddV2, OrderV2 } from "@/types/inventoryV2";
import { SourceTypeEnum } from "@/types/common";
import { orderAdd } from "@/apis/inventoryv2";
import { RcFile } from "antd/es/upload";
import { upload } from "@/utils/file/upload";
import { WFile } from "@/types/file";
import { generateLotV2 } from "@/utils/codeParse";


interface PurOrderData extends PurchaseOrderItem {
  maxQty: number;
  sendQty: number;
}
interface ProductQtyData extends ParsedProQty {
  purchaseOrders: Array<PurOrderData>;   //匹配到的采购单
  inventoryOrders: Array<OrderV2>;   //生成的送货单单
  // sentQty: number;                             //已送货的数量
  sendQty: number;                             //本次送货数量
  remainQty: number;                            //剩余数量
}
interface MatchSupplierOrderProps extends ModalProps {
  open: boolean,
  onClose: () => void;
  onSuccess: () => void;
}

const MatchSupplierOrder: FC<MatchSupplierOrderProps> = ({ open, onClose, onSuccess, ...modalProps }) => {
  const { t } = useTranslation();

  const [current, setCurrent] = useState<number>(0);
  const [supplierId, setSupplierId] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<string[]>([]);
  const [productQtyList, setProductQtyList] = useState<ProductQtyData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const fileRef = useRef<RcFile | null>(null)


  // useEffect(() => {
  //   setPurOrderData([])
  // }, [productQtyList])

  const handlePrev = () => {
    setCurrent(current - 1);
  };
  const handleDeleteSelected = () => {
    setErrorCodes(last => last.filter(code => !selectedRowKeys.includes(code)))
    setProductQtyList(productQtyList.filter(p => !selectedRowKeys.includes(p.productCode)));
    setSelectedRowKeys([]);
  };

  //去第二步匹配采购单
  const handleToMatchPur = () => {
    setCurrent(1);
    handleMatchPurOrder();
  }

  const getMaxItemSendQty = (purOrderItem: PurchaseOrderItem) => {
    let maxQuantity = 0
    let max = Number(purOrderItem.count || 0);
    max = max + Number(purOrderItem.spareCount || 0);
    let curQuantity: number = 0;

    if (purOrderItem.inventoryItems && purOrderItem.inventoryItems.length > 0) {
      purOrderItem.inventoryItems.forEach(o => {
        if (o?.order?.opType === OperateEnum.TRANSACTION) {  //正常采购入库
          curQuantity += Number(o.count)
        } else {    //退库
          if (o?.order?.confirmState === ConfirmedEnum.CONFIRM) { //确认的单据：出库- 入库+
            if (o?.order?.type === ChangeEnum.IN) curQuantity += Number(o.count)
            else curQuantity -= Number(o.count)
          } else {//未确认的单据： 入库+ 出库不管
            if (o?.order?.type === ChangeEnum.IN) curQuantity += Number(o.count)
          }
        }
      });
    }
    maxQuantity = Math.max(Number(max) - curQuantity, 0)
    return maxQuantity;
  }

  const setMatchPurRes = (purData: PurOrderData[]) => {
    // 复制一份产品数据，初始化 purchaseOrders
    const updatedProData: ProductQtyData[] = productQtyList.map(data => ({ ...data, purchaseOrders: [] }));

    // 遍历每个产品进行匹配
    const resultData = updatedProData.map(product => {
      const productId = product.product?.id;

      // 在 purData 中找出匹配的采购单
      const matchedPurOrders: PurOrderData[] = purData
        .filter(pur => pur.productId === productId)
        .map(pur => ({ ...pur }));

      // 初始化需分配数量
      let remainQty = product.count;

      // 按顺序分配给采购单
      for (let i = 0; i < matchedPurOrders.length; i++) {
        const canSend = Math.min(matchedPurOrders[i].maxQty, remainQty);
        matchedPurOrders[i].sendQty = canSend;
        remainQty -= canSend;
      }

      // 过滤掉 sendQty 为 0 的采购单
      const validPurOrders = matchedPurOrders.filter(p => p.sendQty > 0);

      return {
        ...product,
        sendQty: product.count - remainQty,
        purchaseOrders: validPurOrders,
        remainQty,
      };
    });

    // 更新状态
    setProductQtyList(resultData);
  }

  const handleMatchPurOrder = async () => {
    setLoading(true);
    try {
      const pageSize = 50;
      const allData: Array<PurchaseOrderItem> = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const res = await itemFetchs({
          body: {
            supplierId,
            keyword: '',
            finishStates: [PurchasefFnishStateEnum.UN_FINISHED, PurchasefFnishStateEnum.FINISHING, PurchasefFnishStateEnum.FINISHED],
            productIds: productQtyList.map(p => p.product!.id)
          },
          pagination: {
            offset,
            take: pageSize,
          },
        });
        allData.push(...(res.body || []));
        const totalCount = res.pagination?.size || 0;
        if (allData.length >= totalCount) {
          hasMore = false;
        } else {
          offset += pageSize;
        }
      }
      let resData: PurOrderData[] = allData
        .sort((a, b) => a.createdAt - b.createdAt)
        .map(pur => {
          return {
            ...pur,
            maxQty: getMaxItemSendQty(pur),
            sendQty: 0,
          }
        })
      setMatchPurRes(resData);
    } catch (error) {
      notification.error({
        message: t('common.error'),
        description: error + '',
      });
    } finally {
      setLoading(false);
    }
  }

  const handleAddOrder = async () => {
    setLoading(true)
    let itemsInfo: OrderAddItemV2[] = [];
    let now = Date.now();
    for (let q = 0; q < productQtyList.length; q++) {
      const data = productQtyList[q];
      for (let p = 0; p < data.purchaseOrders.length; p++) {
        const pur = data.purchaseOrders[p];
        let lot = generateLotV2(pur.product?.code ?? '', pur?.order?.supplier?.code ?? '', ++now);
        if (!!!lot) {
          notification.error({ message: t('common.lotGetErr'), description: `${t('purchase.printCode')}：${pur?.product?.code ?? ''}，${t('purchase.code')}：${pur?.order?.supplier?.code ?? ''}` })
          setLoading(false);
          return
        }
        itemsInfo.push({
          sourceType: SourceTypeEnum.PURCHASE_ITEM,
          sourceId: pur.id,
          productId: pur.productId,
          count: pur.sendQty,
          lot,
          thdlot: '',
          desc: '',
          locations: [],
        })
      }
    }
    if (itemsInfo.length === 0) {
      setLoading(false)
      notification.error({ message: t('purchase.noSendInfo') })
      return
    }

    let orderAddInfo: OrderAddV2 = {
      type: ChangeEnum.PRE_IN,
      opType: OperateEnum.TRANSACTION,
      desc: '',
      preTime: null,
      arrivalAt: null,
      items: itemsInfo,
    }
    let fileRes: WFile | undefined = undefined;
    try {
      if (fileRef.current) {
        fileRes = await upload({ name: fileRef.current.name, size: fileRef.current.size, file: fileRef.current });
      } else {
        notification.error({ message: t('purchase.fileLoss') })
        setLoading(false);
        return
      }
      let batchInOrderAddInfo: BatchInOrderAdd = {
        supplierId,
        fileId: fileRes?.id ?? '',
        items: []
      };

      let order = await orderAdd(orderAddInfo);
      notification.success({ message: t('purchase.importDone'), description: order.number })

      let newProductQtyList = [...productQtyList]
      for (let i = 0; i < newProductQtyList.length; i++) {
        let data = newProductQtyList[i];
        data.purchaseOrders.forEach(pur => {
          batchInOrderAddInfo.items.push({
            purItemId: pur.id,
            sioOrderItem: order?.items?.find(i => i.sourceId === pur.id)?.id ?? '',
            produceCode: data.productCode,
            totalCount: data.count,
            inCount: pur.sendQty,
            purCount: Number(pur.count || 0) + Number(pur.spareCount || 0),
            remainCount: data.remainQty
          })
        })
        newProductQtyList[i].inventoryOrders = !!newProductQtyList[i].sendQty ? [{ ...order }] : []
      }
      setProductQtyList(newProductQtyList)
      let addRes = await batchInOrderAdd(batchInOrderAddInfo)
      setCurrent(2)
    } catch (error) {

    } finally {
      setLoading(false)
    }
  }

  // 导入数据
  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    setErrorCodes([]);
    fileRef.current = file;
    try {
      const addList = await parseData(file, t);
      if (addList.length > 0) {
        const productListRes = await productList({ params: { strs: addList.map(a => a.productCode) } });
        const productListMap: Record<string, Product> = {};
        productListRes.body.forEach(p => {
          productListMap[p.code] = { ...p }
        });
        let notProsList: string[] = [];
        for (let index = 0; index < addList.length; index++) {
          let code = addList[index].productCode
          if (!!productListMap[code]) {
            addList[index].product = { ...productListMap[code] }
          } else notProsList.push(code)
        }
        if (notProsList.length > 0) {
          setErrorCodes(notProsList)
          notification.error({
            message: t('apiError.error26'),
            description: notProsList.join(', ')
          })
        }
        setProductQtyList([...addList].map(l => ({
          ...l,
          purchaseOrders: [],
          inventoryOrders: [],
          sendQty: 0,
          remainQty: l.count
        })))
        notification.success({ placement: 'top', message: t("requirement.parseDataSuccess", { count: addList.length }) });
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("requirement.invalidTemplate") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };

  const templateCol: ColumnsType<ProductQtyData> = [
    {
      title: t("warehouse.productCode"),
      dataIndex: "productCode",
      key: "productCode",
      width: 120,
      render: (text: string) => <SText style={{ width: 120, color: errorCodes.includes(text) ? "red" : "unset" }} text={text} />,
    },
    {
      title: t("warehouse.productName"),
      dataIndex: ["product", "name"],
      key: "name",
      width: 160,
      render: (text: string) => <SText style={{ width: 160 }} text={text} />,
    },
    {
      title: t("warehouse.productModel"),
      dataIndex: ["product", "model"],
      key: "model",
      width: 120,
      ellipsis: true,
      render: (text: string) => <SText text={text} />,
    },
    {
      title: t("warehouse.uom"),
      dataIndex: ["product", "uom"],
      key: "uom",
      width: 80,
      render: (text: string) => <SText text={text} />,
    },
    {
      title: t("purchase.purNo"),
      dataIndex: "purchaseOrders",
      key: "purchaseOrders",
      width: 180,
      hidden: current === 0,
      render: (_, record) => {
        return record.purchaseOrders.map(pur => (
          <div style={{ width: "100%" }}>
            <SText text={pur?.number ?? ''} />
          </div>
        ));
      },
    },
    {
      title: t("purchase.deliveryNumber"),
      dataIndex: "inventoryOrders",
      key: "inventoryOrders",
      width: 180,
      hidden: current !== 2,
      render: (_, record) => {
        return record.inventoryOrders.map(order => (
          <div style={{ width: "100%" }}>
            <SText text={order?.number ?? ''} />
          </div>
        ));
      },
    },
    {
      title: t("purchase.allSendQty"),
      dataIndex: "count",
      key: "count",
      width: 100,
      fixed: "right",
      render: (value: number) => {
        return <SText text={(value ?? 0) + ''} />;
      },
    },
    // {
    //   title: t("purchase.sentQty"),
    //   dataIndex: "sentQty",
    //   key: "sentQty",
    //   width: 100,
    //   fixed:"right",
    //   hidden: current !== 1,
    //   render: (value: number) => {
    //     return <SText text={(value ?? 0) + ''} />;
    //   },
    // },
    {
      title: t("purchase.sendQty"),
      dataIndex: "sendQty",
      key: "sendQty",
      width: 100,
      fixed: "right",
      hidden: current === 0,
      render: (value: number) => {
        return <SText text={(value ?? 0) + ''} />;
      },
    },
    {
      title: t("purchase.remainQTy"),
      dataIndex: "remainQty",
      key: "remainQty",
      width: 100,
      fixed: "right",
      hidden: current === 0,
      render: (value: number) => {
        return <SText text={(value ?? 0) + ''} />;
      },
    },
  ];

  // const purCol: ColumnsType<PurchaseOrderItem> = [
  //   {
  //     title: <SText text={t('purchase.orderNumber')} />,
  //     dataIndex: 'number',
  //     key: 'number',
  //     width: 180,
  //     fixed: "left",
  //     render: (_, item) => <SText text={item?.number} style={{ width: "100%" }} />
  //   },
  //   {
  //     title: <SText text={t('purchase.inventoryCode')} />,
  //     dataIndex: ['product', 'code'],
  //     key: 'productCode',
  //     width: 120,
  //     render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />
  //   },
  //   {
  //     title: <SText text={t('purchase.supplierCode')} />,
  //     dataIndex: ['order', 'supplier', 'code'],
  //     key: 'supplierCode',
  //     width: 120,
  //     render: (_, item) => <SText text={item?.order?.supplier?.code} style={{ width: "100%" }} />
  //   },
  //   {
  //     title: <SText text={t('purchase.supplierName')} />,
  //     dataIndex: ['order', 'supplier', 'name'],
  //     key: 'supplierName',
  //     width: 150,
  //     render: (_, item) => <SText text={item?.order?.supplier?.name ?? ""} style={{ width: "100%" }} />
  //   },
  //   {
  //     title: <SText text={t('purchase.purchaseQuantity')} />,
  //     dataIndex: 'count',
  //     key: 'count',
  //     width: 100,
  //     render: (_, item) => <SText text={item?.count?.toString() ?? ""} style={{ width: "100%" }} />
  //   },
  //   {
  //     title: <SText text={t('purchase.spareRate')} />,
  //     dataIndex: 'spareRate',
  //     key: 'spareRate',
  //     width: 80,
  //     render: (_, item) => <SText text={Number(item?.spareRate ?? "") + ''} />
  //   },
  //   {
  //     title: <SText text={t('purchase.spareCount')} />,
  //     dataIndex: 'spareCount',
  //     key: 'spareCount',
  //     width: 80,
  //     render: (_, item) => <SText text={item.spareCount + ''} />
  //   }
  // ];

  const steps = [
    {
      title: t("purchase.import"),
      content: (
        <Fragment>
          <STableLayout
            headerChildren={
              <Flex gap={6}>
                <Upload
                  accept=".xlsx,.xls"
                  beforeUpload={handleImport}
                  showUploadList={false}
                  disabled={uploading || !!!supplierId}
                >
                  <Button disabled={uploading || !!!supplierId} loading={uploading}>{t("requirement.inputTemp")}</Button>
                </Upload>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteSelected}
                  disabled={selectedRowKeys.length === 0}
                >
                  {t("warehouse.delete")}
                </Button>
              </Flex>
            }
            columns={templateCol as Array<AnyObject>}
            dataSource={productQtyList}
            rowKey={(record) => record.productCode}
            pagination={false}
            locale={{ emptyText: <Empty description={t("warehouse.noData")} /> }}
            total={productQtyList.length}
            extHeight={70}
            loading={uploading}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
            }}
            scrollX={1200}
          />
        </Fragment>
      ),
    },
    {
      title: t("purchase.matchPur"),
      content: <STableLayout
        columns={templateCol as Array<AnyObject>}
        dataSource={productQtyList}
        // headerChildren={
        //   <Button loading={loading} onClick={handleMatchPurOrder} >{t('purchase.重新匹配采购单')}</Button>
        // }
        headerChildren={null}
        pagination={false}
        extHeight={70}
        total={productQtyList.length}
        loading={loading}
        scrollX={1200}
      />,
    },
    {
      title: t("purchase.done"),
      content: <STableLayout
        columns={templateCol as Array<AnyObject>}
        dataSource={productQtyList}
        // headerChildren={
        //   <Button loading={loading} onClick={handleMatchPurOrder} >{t('purchase.重新匹配采购单')}</Button>
        // }
        headerChildren={null}
        pagination={false}
        extHeight={70}
        total={productQtyList.length}
        loading={loading}
        scrollX={1200}
      />,
    },
  ];

  return (
    <Modal
      title={t('purchase.importSuppOrders')}
      open={open}
      onCancel={onClose}
      width={1200}
      centered
      maskClosable={false}
      footer={
        <Flex justify="space-between" style={{ width: "100%" }}>
          <Flex gap={8}>
            {current === 0 && (
              <Button type="primary" onClick={handleToMatchPur}
                disabled={!!!supplierId || productQtyList.length === 0 || errorCodes.length > 0}>
                {t("warehouse.next")}
              </Button>
            )}
            {current === 1 && (
              <Fragment>
                <Button onClick={handlePrev}>{t("warehouse.previous")}</Button>
                <Button type="primary" onClick={handleAddOrder} disabled={loading}>{t("warehouse.next")}</Button>
              </Fragment>
            )}
          </Flex>
          {
            current === 2 && (
              <Button type="primary" onClick={onClose}>{t('common.confirm')}</Button>
            )
          }
        </Flex>
      }
    >
      <Steps current={current} items={steps.map(item => ({ title: item.title }))} style={{ marginBottom: 24 }} />
      <Flex vertical style={{ width: "100%", height: "70vh", overflow: "hidden" }}>
        <Form.Item label={t('requirement.supplier')} style={{ marginBottom: 4 }} >
          <SupplierSelect disabled={current !== 0} placeholder={t("purchase.plSupplier")} onChange={setSupplierId} />
        </Form.Item>
        {steps[current].content}
      </Flex>
    </Modal>
  )
};
export default MatchSupplierOrder;